import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  ExpandMore as ExpandMoreIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import type { PublicTowerViewUnit } from '../../services/superPickerPublicApi';
import { useRevealedSection } from '../../services/revealedPrices';
import { buildPublicUnitSyntheticKey, type PublicUnitForMenu } from './unit/types';
import { isUnitStatusNotReleased } from '../../../utils/formatting';
import {
  useIsTabletLandscape,
  useMobileOptimizedStyles,
  useScrollableContainerStyles,
} from '../../hooks/mobile-hooks';
import { parseUnitNumber } from '../../utils/parseUnitNumber';

interface TowerViewSectionProps {
  /** Live, masked-pricing tower-view rows from the public API. */
  units: PublicTowerViewUnit[];
  /**
   * Project the units belong to. Required for per-cell price reveal — the
   * cell click handler builds a `PublicUnitForMenu` with this `projectId`
   * so the parent's `UnitActionsMenu` can route to the towerView reveal
   * endpoint. Omit on legacy mounts that don't yet support reveal.
   */
  projectId?: string | null;
  /**
   * Fired when a non-empty cell is clicked. The parent (e.g.
   * `ProjectDetailPage`) opens the unit-actions menu — typically with
   * only the `Reveal prices` item visible — at the cursor.
   */
  onCellClick?: (unit: PublicUnitForMenu, mouseX: number, mouseY: number) => void;
  /**
   * External multi-select bedroom filter (numeric ids — e.g. `[2, 3]`).
   * Mirrors the agent-side `sitePlanBedroomFilter` plumbing in
   * `TopViewingSitePlanDetail`: the All Blocks tower view no longer
   * owns its own Bedroom dropdown; instead it consumes the value the
   * parent's `SitePlanSectionDetail` produces, so a single filter UI
   * drives both the site-plan dots and the tower grid. `[]` / omitted
   * = no filter.
   */
  bedroomFilter?: number[];
  /**
   * External multi-select unit-type filter (strings, e.g. `['1A', '2C']`).
   * Same source/contract as `bedroomFilter`. `[]` / omitted = no filter.
   */
  unitTypeFilter?: string[];
  /**
   * Expanded set of unit-type CODES that map to the selected
   * descriptions in `unitTypeFilter`. Mirrors the agent-side
   * `sitePlanUnitTypeFilterExpandedUnitTypes`: a cell matches when
   * EITHER its `unitTypeDescription` is in `unitTypeFilter` OR its
   * `unitType` code is in this set. Required because some upstream
   * payload rows carry only the code (e.g. `C5-PREMIUM`) with no
   * description. Optional / defaults to an empty set.
   */
  unitTypeFilterCodes?: Set<string>;
  /**
   * Optional controlled Stack filter. When supplied (together with
   * `onStackFilterChange`), the Stack dropdown renders the value the
   * parent owns instead of private state — so the selection can also
   * drive the sibling `SitePlanSectionDetail` (filtering + pulsing the
   * matching dots). Values are numeric stack ids as strings plus the
   * `'ODD'` / `'EVEN'` sentinels. Both props must be provided together;
   * omit them to keep the legacy self-contained behaviour.
   */
  stackFilter?: string[];
  onStackFilterChange?: (next: string[]) => void;
  /** Returns true when the unit is already in the floor-plan compare cart. */
  isInCompareCart?: (unitKey: string, projectId?: string | null) => boolean;
}

/**
 * Render-friendly cell derived from a `PublicTowerViewUnit`. `floor` /
 * `stack` are parsed from the agent-formatted `#<floor>-<stack>` unit
 * number; rows whose unit number can't be parsed are dropped.
 */
interface TowerCell {
  unitKey: string;
  unit: string;
  floor: number;
  stack: number;
  blockNumber: string;
  /** Unit-type code (e.g. `A1`, `A1(d)`). */
  unitType: string;
  /** Free-text description (e.g. `1 Bedroom`); empty when missing. */
  unitTypeDescription: string;
  bedroom: number;
  /**
   * Carried through from `PublicTowerViewUnit.bathroom` so the
   * unit-actions menu's Floor Plan / Compare items receive the same
   * `PublicUnitForMenu` shape they expect from `AvailableUnitsSection`.
   */
  bathroom: number;
  area: number;
  /** Floor-plan asset path — required for the Floor Plan dialog/cart. */
  floorplan: string | null;
  /** Raw FloorNumber (e.g. "G", "01") for compare / menu display. */
  floorNumber: string | null;
  /** Project name carried for the compare modal's grouping header. */
  projectName: string;
  priceDisplay: string | null;
  psfDisplay: string | null;
  isSold: boolean;
  isNotReleased: boolean;
}

const toTowerCells = (units: PublicTowerViewUnit[]): TowerCell[] => {
  const out: TowerCell[] = [];
  for (const u of units) {
    const parsed = parseUnitNumber(u.unitNumber, u.floorNumber);
    if (parsed.floor == null || parsed.stack == null) continue;
    out.push({
      unitKey: u.key,
      unit: u.unitNumber,
      floor: parsed.floor,
      stack: parsed.stack,
      blockNumber: u.blockNumber || '',
      unitType: u.unitType || '',
      unitTypeDescription: u.unitTypeDescription || '',
      bedroom: Number.isFinite(u.bedroom) ? u.bedroom : 0,
      bathroom: Number.isFinite(u.bathroom) ? u.bathroom : 0,
      area: Number.isFinite(u.areaSqft) ? u.areaSqft : 0,
      floorplan: u.floorplan ?? null,
      floorNumber: u.floorNumber ?? null,
      projectName: u.projectName ?? '',
      priceDisplay: u.price ? `$${u.price}` : null,
      psfDisplay: u.psf ? `$${u.psf}` : null,
      isSold: (u.unitStatus || '').toString().toLowerCase().includes('sold'),
      isNotReleased: isUnitStatusNotReleased(u.unitStatus),
    });
  }
  return out;
};

type DisplayOption = 'bedroom' | 'price' | 'psf' | 'size';
const ALL_DISPLAY_OPTIONS: DisplayOption[] = ['bedroom', 'price', 'psf', 'size'];

const formatCurrency = (v: number) => `$${v.toLocaleString('en-SG')}`;

/**
 * One bucket inside a stack header. Mirrors the agent-side `grouped`
 * array in `TopViewingSitePlanDetail.tsx` (~line 5369): a stack can
 * legitimately hold cells of more than one unit-type / size, and we
 * surface every distinct `(description, size)` combination as its own
 * two-line block in the header.
 */
interface StackGroup {
  /** Bedroom-style description, e.g. `"1BR"` (the agent rewrites
   *  `"Bedroom"` → `"BR"`). Empty when the payload has no description. */
  description: string;
  /** Distinct unit-type codes in this group, e.g. `["A1(d)", "A1"]`. */
  unitTypes: string[];
  /** Size in sqft for the group; `null` when not available. */
  size: number | null;
}

/**
 * Per-stack column header. The agent-side flow keeps a *list* of groups
 * (description + codes + size) per stack instead of a single
 * representative cell, so we mirror that here.
 */
interface StackHeader {
  stack: number;
  /** Every bedroom value seen in this stack (used for the parent's
   *  bedroom filter — a stack is shown when any of its bedrooms
   *  matches, which mirrors the agent's cell-level
   *  `unitMatchesFilter`). */
  bedrooms: number[];
  /** Every unit-type code seen in this stack (used for the parent's
   *  unit-type filter, same reasoning as `bedrooms`). */
  unitTypes: string[];
  /** Header rendering groups (`description` + codes + size). */
  groups: StackGroup[];
  /** First non-empty bedroom seen in the stack — used by the
   *  bedroom-color swatch in the cell body. */
  representativeBedroom: number;
}

interface BlockColumns {
  blockNumber: string;
  stacks: StackHeader[];
}

/**
 * Sentinel bedroom value reserved for penthouse units — mirrors
 * `PENTHOUSE_FILTER_VALUE` in `TopViewingSitePlanDetail`. Penthouses are
 * coloured independently of their numeric bedroom count.
 */
const PENTHOUSE_FILTER_VALUE = -1;

/**
 * Bedroom colour swatch — mirrors `getBedroomColor` from the agent app so the
 * "Bedroom color" toggle visually matches what users see in `TopViewingSitePlanDetail`.
 */
const getBedroomColor = (bedroom: number | null): string => {
  const palette: Record<number, string> = {
    1: '#1565c0',
    2: '#2e7d32',
    3: '#6d4c41',
    4: '#8e24aa',
    5: '#00897b',
    [PENTHOUSE_FILTER_VALUE]: '#f9a825',
  };
  if (bedroom == null) return 'rgba(var(--color-text-rgb), 0.4)';
  return palette[bedroom] ?? '#546e7a';
};

/**
 * Penthouse detection — mirrors `isPenthouseText` / `isPenthouseUnit` in
 * `TopViewingSitePlanDetail`. A unit is treated as a penthouse when its
 * unit-type description or code contains "penthouse" / the standalone
 * abbreviation "PH".
 */
const isPenthouseText = (value: unknown): boolean => {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return false;
  return (
    raw.includes('penthouse')
    || raw.includes('penthhouse')
    || /\bph\b/.test(raw)
    || raw.endsWith('ph')
  );
};
const isPenthouseUnit = (u: { unitType?: string | null; unitTypeDescription?: string | null } | null | undefined): boolean =>
  !!u && (isPenthouseText(u.unitTypeDescription) || isPenthouseText(u.unitType));

export const TowerViewSection: React.FC<TowerViewSectionProps> = ({
  units,
  projectId = null,
  onCellClick,
  bedroomFilter = [],
  unitTypeFilter = [],
  unitTypeFilterCodes,
  stackFilter: stackFilterProp,
  onStackFilterChange,
  isInCompareCart,
}) => {
  const mobileOptimizedStyles = useMobileOptimizedStyles();
  const scrollableStyles = useScrollableContainerStyles();
  const isTabletLandscape = useIsTabletLandscape();
  // Map of synthetic unit key → unmasked prices for this project (if the
  // recipient has paid to reveal any). Used to swap each cell's masked
  // `priceDisplay` / `psfDisplay` for the real numeric value.
  const revealedUnits = useRevealedSection('towerView', projectId);
  const formatRevealedDollar = (value: number | null | undefined): string | null => {
    if (value == null || !Number.isFinite(value) || value <= 0) return null;
    return `$${Math.round(value).toLocaleString('en-SG')}`;
  };
  const [expanded, setExpanded] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [bedroomColor, setBedroomColor] = useState(false);
  // Stack filter values are strings: numeric stack ids (e.g. "3"), or the
  // sentinel values 'ODD' / 'EVEN' which match all odd/even stacks.
  // Controlled by the parent when `stackFilter` + `onStackFilterChange`
  // are supplied (so the site-plan dots can react to the same selection);
  // otherwise we own a private copy so legacy mounts keep working.
  const [internalStackFilter, setInternalStackFilter] = useState<string[]>([]);
  const stackFilter = stackFilterProp ?? internalStackFilter;
  const setStackFilter = (next: string[]): void => {
    if (onStackFilterChange) onStackFilterChange(next);
    else setInternalStackFilter(next);
  };
  const [displayOptions, setDisplayOptions] = useState<DisplayOption[]>([
    'bedroom',
    'price',
    'psf',
    'size',
  ]);

  const cells = useMemo(() => toTowerCells(units), [units]);

  // Build the block → stacks → floors matrix. The intermediate
  // accumulator collects every cell per (block, stack) so we can
  // compute the grouped header (multiple unit types per stack) and the
  // bedroom / unit-type sets used by the parent-supplied filters.
  const { blocks, floors } = useMemo(() => {
    interface StackAccumulator {
      stack: number;
      cells: TowerCell[];
    }
    const blockMap = new Map<string, Map<number, StackAccumulator>>();
    const floorSet = new Set<number>();
    for (const u of cells) {
      floorSet.add(u.floor);
      const stackMap =
        blockMap.get(u.blockNumber) ?? new Map<number, StackAccumulator>();
      const acc = stackMap.get(u.stack) ?? { stack: u.stack, cells: [] };
      acc.cells.push(u);
      stackMap.set(u.stack, acc);
      blockMap.set(u.blockNumber, stackMap);
    }

    // Compress the cells of a single stack into one `StackHeader`,
    // following the agent-side recipe at `TopViewingSitePlanDetail.tsx`
    // — deduplicate by `unitType` code, build entries carrying the
    // description + codes + size, then merge entries that share both
    // `description` and `size`.
    const buildStackHeader = (acc: StackAccumulator): StackHeader => {
      const seenCodes = new Set<string>();
      type Entry = { description: string; unitTypes: string[]; size: number | null };
      const entries: Entry[] = [];
      const bedroomsSet = new Set<number>();
      // Track unit-type *descriptions* (e.g. `"3 Bedroom"`) rather than
      // codes (`"C5"`, `"C6"`). The parent's Unit Type dropdown is
      // populated from `SitePlanSectionDetail.stackInfos`, which stores
      // `unitTypeDescription || unitType` per stack — so to make the
      // selected dropdown value reach the tower-view filter the two
      // sides must speak the same vocabulary. Storing descriptions
      // also implicitly maps `(C5, C6) → "3 Bedroom"`, mirroring the
      // agent-side `sitePlanUnitTypeFilterExpandedUnitTypes` expansion.
      const unitTypesSet = new Set<string>();
      let representativeBedroom = 0;
      for (const cell of acc.cells) {
        if (Number.isFinite(cell.bedroom) && cell.bedroom > 0) {
          bedroomsSet.add(cell.bedroom);
          if (representativeBedroom === 0) representativeBedroom = cell.bedroom;
        }
        const filterKey = (cell.unitTypeDescription || cell.unitType || '').trim();
        if (filterKey) unitTypesSet.add(filterKey);
        const codeKey = cell.unitType || `__desc:${cell.unitTypeDescription}`;
        if (seenCodes.has(codeKey)) continue;
        seenCodes.add(codeKey);
        // Match the agent's `Bedroom` → `BR` rewrite so headers read
        // `1BR` instead of `1 Bedroom`.
        const description = (cell.unitTypeDescription || '').replace(/Bedroom/gi, 'BR');
        const size = Number.isFinite(cell.area) && cell.area > 0 ? cell.area : null;
        entries.push({
          description,
          unitTypes: cell.unitType ? [cell.unitType] : [],
          size,
        });
      }

      // Merge entries sharing the same `(description, size)` so a
      // stack with `A1(d)` + `A1` (same description, same size)
      // collapses to a single header line `A1(d) / A1 / 463 sqft`.
      const grouped: StackGroup[] = [];
      for (const entry of entries) {
        const existing = grouped.find(
          (g) => g.description === entry.description && g.size === entry.size,
        );
        if (existing) {
          for (const t of entry.unitTypes) {
            if (t && !existing.unitTypes.includes(t)) existing.unitTypes.push(t);
          }
        } else {
          grouped.push({
            description: entry.description,
            unitTypes: [...entry.unitTypes],
            size: entry.size,
          });
        }
      }
      if (grouped.length === 0) {
        grouped.push({ description: '', unitTypes: [], size: null });
      }

      return {
        stack: acc.stack,
        bedrooms: Array.from(bedroomsSet),
        unitTypes: Array.from(unitTypesSet),
        groups: grouped,
        representativeBedroom,
      };
    };

    const blockList: BlockColumns[] = Array.from(blockMap.entries())
      .map(([blockNumber, stackMap]) => ({
        blockNumber,
        stacks: Array.from(stackMap.values())
          .sort((a, b) => a.stack - b.stack)
          .map(buildStackHeader),
      }))
      // Sort blocks by leading numeric component, falling back to a
      // locale string compare. Mirrors the agent's block-number sort
      // in `SuperPickerApp.tsx :: buildTowerViewDataFromUnits` so the
      // public side renders blocks in the same ascending order
      // (e.g. `Block 1, Block 2, Block 10` instead of lexicographic
      // `Block 1, Block 10, Block 2`).
      .sort((a, b) => {
        const numA = parseInt(a.blockNumber.replace(/\D/g, ''), 10) || 0;
        const numB = parseInt(b.blockNumber.replace(/\D/g, ''), 10) || 0;
        if (numA !== numB) return numA - numB;
        return a.blockNumber.localeCompare(b.blockNumber);
      });
    return {
      blocks: blockList,
      floors: Array.from(floorSet).sort((a, b) => b - a),
    };
  }, [cells]);

  const unitLookup = useMemo(() => {
    const map = new Map<string, TowerCell>();
    for (const u of cells) {
      map.set(`${u.blockNumber}|${u.floor}|${u.stack}`, u);
    }
    return map;
  }, [cells]);

  const allStacks = useMemo(
    () =>
      Array.from(new Set(cells.map((u) => u.stack))).sort((a, b) => a - b),
    [cells],
  );

  // Distinct bedroom values present in the tower (sorted asc), with the
  // penthouse sentinel appended when any unit reads as a penthouse — used
  // to render the legend next to the "Bedroom color" toggle. Mirrors the
  // bedroom + PH treatment in `TopViewingSitePlanDetail`.
  const distinctBedrooms = useMemo(() => {
    const numeric = new Set<number>();
    let hasPenthouse = false;
    for (const u of cells) {
      if (isPenthouseUnit(u)) {
        hasPenthouse = true;
        continue;
      }
      if (Number.isFinite(u.bedroom) && u.bedroom > 0) numeric.add(u.bedroom);
    }
    const sorted = Array.from(numeric).sort((a, b) => a - b);
    if (hasPenthouse) sorted.push(PENTHOUSE_FILTER_VALUE);
    return sorted;
  }, [cells]);

  const showPrice = displayOptions.includes('price');
  const showPsf = displayOptions.includes('psf');
  const showBedroom = displayOptions.includes('bedroom');
  const showSize = displayOptions.includes('size');
  const isCompact = displayOptions.length === 0;
  const hasDense = showPrice || showPsf || showSize;
  const cellWidth = isCompact ? 40 : hasDense ? 100 : 88;

  return (
    <Card sx={{ mb: 2, position: 'relative', ...mobileOptimizedStyles }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            All Blocks Tower View
          </Typography>
          <IconButton
            size="small"
            onClick={() => setExpanded((v) => !v)}
            sx={{ ml: 'auto' }}
          >
            <ExpandMoreIcon
              sx={{
                transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          {/* Controls row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={bedroomColor}
                  onChange={(e) => setBedroomColor(e.target.checked)}
                />
              }
              label={
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}
                >
                  Bedroom color
                </Typography>
              }
              sx={{ ml: 0 }}
            />

            {/* Legend — bedroom palette (incl. PH when any penthouse is
                present), mirrors the Presentation View legend in
                `TopViewingSitePlanDetail`. Only rendered when the
                Bedroom color toggle is on. */}
            {bedroomColor && distinctBedrooms.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', fontWeight: 600 }}>
                  Bedroom:
                </Typography>
                {distinctBedrooms.map((br) => (
                  <Box key={`tv-legend-${br}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 14, height: 14, borderRadius: '3px', bgcolor: getBedroomColor(br), border: '1px solid rgba(var(--color-text-rgb), 0.35)' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.85)' }}>
                      {br === PENTHOUSE_FILTER_VALUE ? 'PH' : `${br}BR`}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto', flexWrap: 'wrap' }}>
              {/*
               * The Bedroom dropdown that used to live here was removed
               * intentionally — the All Blocks Tower View is now driven
               * by the Bedroom + Unit Type filters in the parent's Site
               * Plan section (`bedroomFilter` / `unitTypeFilter` props).
               * Mirrors the agent-side flow in
               * `TopViewingSitePlanDetail.tsx` where a single filter UI
               * controls both surfaces.
               */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="tower-stack-filter">Stack</InputLabel>
                <Select
                  labelId="tower-stack-filter"
                  multiple
                  value={stackFilter.length === 0 ? ['any'] : stackFilter}
                  onChange={(e) => {
                    const raw = e.target.value as string[];
                    if (raw.includes('any')) {
                      const withoutAny = raw.filter((v) => v !== 'any');
                      setStackFilter(stackFilter.length > 0 ? [] : withoutAny);
                    } else {
                      setStackFilter(raw);
                    }
                  }}
                  label="Stack"
                  renderValue={(selected) => {
                    const filtered = (selected as string[]).filter((v) => v !== 'any');
                    if (filtered.length === 0) return 'Any';
                    return filtered
                      .map((v) => {
                        if (v === 'ODD') return 'Odd';
                        if (v === 'EVEN') return 'Even';
                        return v.padStart(2, '0');
                      })
                      .join(', ');
                  }}
                >
                  <MenuItem value="any">Any</MenuItem>
                  <MenuItem value="ODD">Odd</MenuItem>
                  <MenuItem value="EVEN">Even</MenuItem>
                  {allStacks.map((s) => (
                    <MenuItem key={s} value={String(s)}>
                      {s.toString().padStart(2, '0')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ minWidth: 90 }}>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', display: 'block', mb: 0.25 }}
                >
                  Show all attributes
                </Typography>
                <Switch
                  checked={displayOptions.length === ALL_DISPLAY_OPTIONS.length}
                  onChange={(e) =>
                    setDisplayOptions(e.target.checked ? [...ALL_DISPLAY_OPTIONS] : [])
                  }
                />
              </Box>

              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel id="tower-display-options-label">Show</InputLabel>
                <Select
                  labelId="tower-display-options-label"
                  multiple
                  value={displayOptions}
                  onChange={(e) =>
                    setDisplayOptions(e.target.value as DisplayOption[])
                  }
                  label="Show"
                  renderValue={(selected) =>
                    selected.length === 0 ? 'Unit no. only' : selected.join(', ')
                  }
                >
                  <MenuItem value="bedroom">
                    <Checkbox
                      size="small"
                      checked={displayOptions.indexOf('bedroom') > -1}
                      sx={{ p: 0.5, mr: 1 }}
                    />
                    Show bedroom type
                  </MenuItem>
                  <MenuItem value="price">
                    <Checkbox
                      size="small"
                      checked={displayOptions.indexOf('price') > -1}
                      sx={{ p: 0.5, mr: 1 }}
                    />
                    Show price
                  </MenuItem>
                  <MenuItem value="psf">
                    <Checkbox
                      size="small"
                      checked={displayOptions.indexOf('psf') > -1}
                      sx={{ p: 0.5, mr: 1 }}
                    />
                    Show PSF
                  </MenuItem>
                  <MenuItem value="size">
                    <Checkbox
                      size="small"
                      checked={displayOptions.indexOf('size') > -1}
                      sx={{ p: 0.5, mr: 1 }}
                    />
                    Show size
                  </MenuItem>
                </Select>
              </FormControl>

              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  alignItems: 'center',
                  border: '1px solid rgba(var(--color-text-rgb), 0.2)',
                  borderRadius: 1,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}
                  disabled={zoom <= 0.5}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
                <Typography variant="caption" sx={{ minWidth: 44, textAlign: 'center' }}>
                  {Math.round(zoom * 100)}%
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setZoom((z) => Math.min(2, +(z + 0.1).toFixed(2)))}
                  disabled={zoom >= 2}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>

          {/* Tower grid scroll container */}
          <Box
            sx={{
              ...scrollableStyles,
              maxHeight: {
                xs: 520,
                md: isTabletLandscape ? 'calc(100vh - 220px)' : 'calc(100vh - 300px)',
              },
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                width: 'fit-content',
                minWidth: '100%',
                // `zoom` is non-standard but used in the agent app — it integer-scales
                // the grid without distorting the pixel grid (unlike `transform`).
                zoom,
                transformOrigin: 'top left',
              }}
            >
              {blocks.map((block) => {
                const stacksAfterStack =
                  stackFilter.length === 0
                    ? block.stacks
                    : block.stacks.filter((s) =>
                        stackFilter.some((sel) => {
                          if (sel === 'ODD') return s.stack % 2 === 1;
                          if (sel === 'EVEN') return s.stack % 2 === 0;
                          return Number(sel) === s.stack;
                        }),
                      );
                // Per-cell match against the parent-supplied site-plan
                // filters — mirrors `unitMatchesFilter` in the agent's
                // `TopViewingSitePlanDetail.tsx` (~line 5269). Key
                // semantics replicated from the agent:
                //   * `PENTHOUSE_FILTER_VALUE` matches iff the unit is a
                //     penthouse (by code or description).
                //   * A numeric bedroom value matches ONLY when the unit
                //     is NOT a penthouse — so a PH cell whose bedroom
                //     happens to be 3 is correctly excluded from the
                //     `3BR` selection, and a non-PH 3BR cell is
                //     excluded from a `Penthouse`-only selection.
                // Cells that don't match render as empty (greyed-out
                // "-" cell), while the surrounding column stays visible
                // as long as any cell in it matches.
                const hasAnyCellFilter =
                  bedroomFilter.length > 0 || unitTypeFilter.length > 0;
                const unitMatchesCellFilter = (
                  u: typeof cells[number] | undefined,
                ): boolean => {
                  if (!u) return false;
                  if (!hasAnyCellFilter) return true;
                  const unitIsPenthouse = isPenthouseUnit(u);
                  if (bedroomFilter.length > 0) {
                    const matchesBedroom = bedroomFilter.some((v) =>
                      v === PENTHOUSE_FILTER_VALUE
                        ? unitIsPenthouse
                        : !unitIsPenthouse
                            && Number.isFinite(u.bedroom)
                            && u.bedroom === v,
                    );
                    if (!matchesBedroom) return false;
                  }
                  if (unitTypeFilter.length > 0) {
                    // Agent parity (`TopViewingSitePlanDetail.tsx ::
                    // unitMatchesFilter`): a unit matches when EITHER its
                    // description is in `unitTypeFilter` OR its code is
                    // in the expanded code-set. We can't rely on the
                    // single `desc || code` key the previous build used
                    // because rows whose payload carries only a code
                    // (e.g. `C5-PREMIUM` with empty description) would
                    // never match a description-only filter.
                    const desc = (u.unitTypeDescription || '').trim();
                    const code = (u.unitType || '').trim();
                    const matchesDesc = !!desc && unitTypeFilter.includes(desc);
                    const matchesCode =
                      !!code
                      && !!unitTypeFilterCodes
                      && unitTypeFilterCodes.has(code);
                    if (!matchesDesc && !matchesCode) return false;
                  }
                  return true;
                };

                // Column visibility — keep stacks where at least one
                // cell matches every active filter (agent's
                // `visibleStacks` recipe at line 5321).
                const visibleStacks = hasAnyCellFilter
                  ? stacksAfterStack.filter((s) =>
                      floors.some((f) =>
                        unitMatchesCellFilter(
                          unitLookup.get(`${block.blockNumber}|${f}|${s.stack}`),
                        ),
                      ),
                    )
                  : stacksAfterStack;
                if (visibleStacks.length === 0) return null;

                // Row visibility is filter-agnostic — agent parity
                // with `TopViewingSitePlanDetail.tsx`, which iterates
                // every floor in `blockData.floors` and lets the
                // per-cell `filteredOut` path blank out cells that
                // don't match. We only drop a row when no visible
                // stack has ANY unit at that floor, which handles the
                // global-`floors`-set case where a floor exists only
                // in another block. Cells that exist but fail the
                // current filter still render as a "-" placeholder
                // alongside the matching cells in the same row.
                const visibleFloors = floors.filter((floor) =>
                  visibleStacks.some((s) =>
                    unitLookup.has(`${block.blockNumber}|${floor}|${s.stack}`),
                  ),
                );
                if (visibleFloors.length === 0) return null;

                return (
                  <Box key={`block-${block.blockNumber}`} sx={{ flex: '0 0 auto' }}>
                    {/* Block header */}
                    <Box
                      sx={{
                        p: 1,
                        textAlign: 'center',
                        fontWeight: 700,
                        borderRadius: '6px 6px 0 0',
                        bgcolor: 'var(--color-accent)',
                        color: 'var(--color-text-inverse)',
                      }}
                    >
                      {block.blockNumber}
                    </Box>

                    {/* Grid (stack headers + cells) */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `40px repeat(${visibleStacks.length}, ${cellWidth}px)`,
                        gridAutoRows: 'auto',
                        gap: '1px',
                        width: 'fit-content',
                        p: '1px',
                        borderRadius: '0 0 6px 6px',
                        bgcolor: 'rgba(var(--color-text-rgb), 0.16)',
                      }}
                    >
                      {/* Top-left corner */}
                      <Box sx={{ bgcolor: 'rgba(var(--color-shadow-rgb), 0.35)' }} />

                      {/* Stack header row */}
                      {visibleStacks.map((s) => (
                        <Box
                          key={`stack-${block.blockNumber}-${s.stack}`}
                          sx={{
                            minHeight: 48,
                            textAlign: 'center',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            py: 0.5,
                            px: 0.25,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            bgcolor: 'rgba(var(--color-shadow-rgb), 0.35)',
                            color: 'var(--color-text-strong)',
                          }}
                        >
                          <Typography component="span" sx={{ fontSize: '0.72rem', fontWeight: 700, lineHeight: 1.2 }}>
                            {s.stack.toString().padStart(2, '0')}
                          </Typography>
                          {/*
                           * Render every (description, codes, size) group
                           * in this stack as its own two-line block,
                           * matching the agent layout:
                           *   Line 1: "1BR"
                           *   Line 2: "A1(d) / A1 / 463 sqft"
                           * (description omitted when empty; size /
                           * codes hyphen-out when missing.)
                           */}
                          {s.groups.map((g, i) => {
                            const sizeStr =
                              g.size != null && Number.isFinite(g.size)
                                ? `${g.size} sqft`
                                : '';
                            const codesStr = g.unitTypes.join(' / ');
                            const detailLine =
                              codesStr && sizeStr
                                ? `${codesStr} / ${sizeStr}`
                                : sizeStr || codesStr || '-';
                            return (
                              <React.Fragment key={i}>
                                {g.description ? (
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: '0.6rem',
                                      fontWeight: 600,
                                      lineHeight: 1.2,
                                      opacity: 0.95,
                                    }}
                                  >
                                    {g.description}
                                  </Typography>
                                ) : null}
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: '0.58rem',
                                    lineHeight: 1.2,
                                    opacity: 0.9,
                                  }}
                                >
                                  {detailLine}
                                </Typography>
                              </React.Fragment>
                            );
                          })}
                        </Box>
                      ))}

                      {/* Floor rows */}
                      {visibleFloors.map((floor) => (
                        <React.Fragment key={`floor-${block.blockNumber}-${floor}`}>
                          {/* Floor label */}
                          <Box
                            sx={{
                              textAlign: 'center',
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              py: 0.25,
                              bgcolor: 'rgba(var(--color-shadow-rgb), 0.35)',
                              color: 'var(--color-text-strong)',
                            }}
                          >
                            {floor}
                          </Box>

                          {/* Cells in this row */}
                          {visibleStacks.map((s) => {
                            const u = unitLookup.get(
                              `${block.blockNumber}|${floor}|${s.stack}`,
                            );
                            // A unit that exists but fails the active
                            // filter renders as empty — same `filteredOut`
                            // → `showAsEmpty` pattern the agent uses
                            // (`TopViewingSitePlanDetail.tsx` ~line 5462).
                            const filteredOut =
                              !!u && hasAnyCellFilter && !unitMatchesCellFilter(u);
                            const isEmpty = !u || filteredOut;
                            const isSold = !isEmpty && u!.isSold;
                            // Not-released units exist but have no prices to
                            // reveal — suppress the menu and show a
                            // `NOT RELEASED` label instead of price.
                            const isNotReleased = !isEmpty && u!.isNotReleased;

                            // Penthouses use the PH sentinel so they pick
                            // up the dedicated PH colour rather than their
                            // numeric bedroom hue — mirrors `bedroomForColor`
                            // in `TopViewingSitePlanDetail`.
                            const bedroomForColor = !isEmpty && isPenthouseUnit(u!)
                              ? PENTHOUSE_FILTER_VALUE
                              : (u?.bedroom ?? null);
                            const bg = isEmpty
                              ? 'rgba(var(--color-shadow-rgb), 0.18)'
                              : isSold
                              ? 'var(--color-danger)'
                              : bedroomColor
                              ? getBedroomColor(bedroomForColor)
                              : 'rgba(var(--color-danger-rgb), 0.95)';

                            const textColor = isEmpty
                              ? 'rgba(var(--color-text-rgb), 0.9)'
                              : 'var(--color-text-inverse)';

                            const borderColor = isEmpty
                              ? 'rgba(var(--color-text-rgb), 0.45)'
                              : isSold
                              ? 'rgba(255,255,255,0.65)'
                              : bedroomColor
                              ? 'rgba(255,255,255,0.5)'
                              : 'rgba(var(--color-text-rgb), 0.35)';

                            const isClickable = !isEmpty && !isNotReleased && Boolean(onCellClick);
                            const isInCompareBasket =
                              !isEmpty && Boolean(isInCompareCart?.(u!.unitKey, projectId));

                            return (
                              <Box
                                key={`cell-${block.blockNumber}-${floor}-${s.stack}`}
                                title={u ? `${u.unit} • ${u.unitType}` : undefined}
                                sx={{
                                  position: 'relative',
                                  width: cellWidth,
                                  px: 0.75,
                                  py: 0.6,
                                  bgcolor: bg,
                                  color: textColor,
                                  border: '1px solid',
                                  borderColor,
                                  borderRadius: 0.5,
                                  boxSizing: 'border-box',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start',
                                  lineHeight: 1.02,
                                  gap: 0.06,
                                  cursor: isClickable ? 'pointer' : 'default',
                                  '&:hover': isClickable
                                    ? { opacity: 0.85, transform: 'scale(1.05)' }
                                    : {},
                                  transition: 'all 0.2s ease',
                                }}
                                onClick={(e) => {
                                  // Empty / not-released cells have no actions.
                                  // Sold units still open the unit-actions menu
                                  // (floor plan / compare / gated items) — agent
                                  // parity with `TopViewingSitePlanDetail.tsx`.
                                  if (!isClickable || !u || !onCellClick) return;
                                  // Build a `PublicUnitForMenu` carrying the
                                  // info reveal/floor-plan callbacks need.
                                  // `unitKey` uses the same synthetic
                                  // `block-unitNumber` shape the masked
                                  // payload uses so the reveal endpoint
                                  // finds the right row.
                                  const synthetic = buildPublicUnitSyntheticKey(
                                    u.blockNumber,
                                    u.unit,
                                  );
                                  const menuUnit: PublicUnitForMenu = {
                                    unitKey: u.unitKey || synthetic,
                                    projectId,
                                    projectName: u.projectName || '',
                                    unitNumber: u.unit,
                                    blockNumber: u.blockNumber || null,
                                    bedroom: u.bedroom,
                                    bathroom: u.bathroom || null,
                                    areaSqft: u.area,
                                    unitStatus: u.isSold ? 'SOLD' : null,
                                    floorplan: u.floorplan,
                                    floorNumber: u.floorNumber,
                                    priceDisplay: u.priceDisplay,
                                    psfDisplay: u.psfDisplay,
                                  };
                                  onCellClick(menuUnit, e.clientX, e.clientY);
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: isEmpty ? '0.9rem' : '0.66rem',
                                    fontWeight: 700,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {isEmpty ? '-' : u!.unit}
                                </Typography>
                                {!isEmpty && showBedroom && (() => {
                                  // Match the agent cell layout in
                                  // `TopViewingSitePlanDetail.tsx` (~line 5547):
                                  //   `${bedroom}BR / ${unitType}` where unitType
                                  // prefers the short code (e.g. `C3-PH`) and
                                  // falls back to the description with `Bedroom`
                                  // → `BR` so PH units read as `3BR / C3-PH`
                                  // instead of the old `3BR / (08)` stack label.
                                  const utStr = (
                                    u!.unitType
                                    || (u!.unitTypeDescription || '').replace(/Bedroom/gi, 'BR')
                                    || ''
                                  ).trim();
                                  return (
                                    <Typography sx={{ fontSize: '0.58rem', opacity: 0.95, whiteSpace: 'nowrap' }}>
                                      {u!.bedroom}BR{utStr ? ` / ${utStr}` : ''}
                                    </Typography>
                                  );
                                })()}
                                {!isEmpty && (
                                  isSold ? (
                                    <Typography sx={{ fontSize: '0.56rem', opacity: 0.95, whiteSpace: 'nowrap' }}>
                                      SOLD
                                    </Typography>
                                  ) : isNotReleased ? (
                                    <>
                                      {showPrice && (
                                        <Typography sx={{ fontSize: '0.56rem', opacity: 0.95, whiteSpace: 'nowrap' }}>
                                          -
                                        </Typography>
                                      )}
                                      {showPsf && (
                                        <Typography sx={{ fontSize: '0.56rem', opacity: 0.95, whiteSpace: 'nowrap' }}>
                                          -
                                        </Typography>
                                      )}
                                    </>
                                  ) : (
                                    (() => {
                                      // Per-cell reveal lookup: if the
                                      // recipient has paid for this unit's
                                      // prices, swap the masked strings for
                                      // the unmasked numbers.
                                      const cellRevealed = revealedUnits?.[
                                        buildPublicUnitSyntheticKey(u!.blockNumber, u!.unit)
                                      ];
                                      const priceCell =
                                        formatRevealedDollar(cellRevealed?.price) ?? u!.priceDisplay ?? '-';
                                      const psfCell = (() => {
                                        const revealedPsf = formatRevealedDollar(cellRevealed?.psf);
                                        if (revealedPsf) return `${revealedPsf} psf`;
                                        return u!.psfDisplay ? `${u!.psfDisplay} psf` : '-';
                                      })();
                                      return (
                                        <>
                                          {showPrice && (
                                            <Typography sx={{ fontSize: '0.56rem', opacity: 0.95, whiteSpace: 'nowrap' }}>
                                              {priceCell}
                                            </Typography>
                                          )}
                                          {showPsf && (
                                            <Typography sx={{ fontSize: '0.56rem', opacity: 0.95, whiteSpace: 'nowrap' }}>
                                              {psfCell}
                                            </Typography>
                                          )}
                                        </>
                                      );
                                    })()
                                  )
                                )}
                                {!isEmpty && showSize && (
                                  <Typography sx={{ fontSize: '0.56rem', opacity: 0.9, whiteSpace: 'nowrap' }}>
                                    {u!.area} sqft
                                  </Typography>
                                )}
                                {isInCompareBasket && (
                                  <ShoppingCartIcon
                                    sx={{
                                      position: 'absolute',
                                      top: 2,
                                      right: 2,
                                      fontSize: '0.75rem !important',
                                      opacity: 0.95,
                                      color: '#FFEB3B',
                                    }}
                                  />
                                )}
                              </Box>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default TowerViewSection;
